import { useState, useEffect, useCallback } from "react";
import {
    PhoneIncoming, PhoneOutgoing, RefreshCw, Loader2, Play, X, PhoneCall, FileText,
} from "lucide-react";
import { Button } from "../ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { TELEPHONY_CALLS_URL } from "../../constants/api";
import RecordingPlayer from "../../pages/engage/ai-caller/RecordingPlayer";
import { cn } from "../../utils/tailwindUtils";
import TranscriptView from "./TranscriptView";
import {
    RECORDING_ENDPOINT, fmtDuration, canTranscribe, CallStatusBadge, TranscriptBadge, TranscribeButton,
} from "./callMeta";

/**
 * Staff phone calls (Twilio / Smartflo) logged against an enquiry or a lead.
 *
 * Props:
 *   enquiryId / customerId — which record's calls to list
 *   refreshKey             — bump it to reload, e.g. a few seconds after CallButton fires
 */
export default function CallHistory({ enquiryId, customerId, refreshKey = 0, className }) {
    const { fetch } = useApiMethods();
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    // One expanded panel per call: the recording player or the transcript.
    const [open, setOpen] = useState({ id: null, what: null });

    const load = useCallback(() => {
        if (!enquiryId && !customerId) return;
        setLoading(true);
        fetch(TELEPHONY_CALLS_URL, { enquiry_id: enquiryId, customer_id: customerId, limit: 50 })
            .then((res) => setCalls(res?.status !== "fail" && Array.isArray(res?.data) ? res.data : []))
            .catch(() => setCalls([]))
            .finally(() => setLoading(false));
    }, [enquiryId, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load, refreshKey]);

    const toggle = (id, what) =>
        setOpen((o) => (o.id === id && o.what === what ? { id: null, what: null } : { id, what }));

    return (
        <div className={cn("bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm", className)}>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <PhoneCall className="w-3.5 h-3.5 text-green-500" />
                    Phone calls
                </h3>
                <Button size="icon" variant="ghost" onClick={load} disabled={loading} title="Refresh"
                    className="h-7 w-7">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
            </div>

            {calls.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-3 text-center">
                    {loading ? "Loading…" : "No calls yet."}
                </p>
            ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {calls.map((c) => {
                        const DirIcon = c.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
                        const isOpen = (what) => open.id === c.id && open.what === what;
                        return (
                            <div key={c.id} className="py-2">
                                <div className="flex items-center gap-2.5">
                                    <DirIcon className={cn("w-3.5 h-3.5 flex-shrink-0",
                                        c.direction === "inbound" ? "text-blue-500" : "text-green-500")} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <CallStatusBadge status={c.status} />
                                            {c.transcript_status !== "done" && <TranscriptBadge status={c.transcript_status} />}
                                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                                {c.agent_name || "—"}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            {c.started_at || "—"} · {fmtDuration(c.duration_seconds)}
                                            {c.customer_number && <> · {c.customer_number}</>}
                                        </div>
                                    </div>
                                    {canTranscribe(c) && <TranscribeButton callId={c.id} onQueued={load} />}
                                    {c.has_transcript && (
                                        <button type="button" onClick={() => toggle(c.id, "transcript")}
                                            title={isOpen("transcript") ? "Hide transcript" : "Show transcript"}
                                            className={cn("p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700",
                                                isOpen("transcript") ? "text-purple-600 dark:text-purple-400"
                                                    : "text-gray-500 hover:text-purple-600 dark:hover:text-purple-400")}>
                                            <FileText className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    {c.has_recording && (
                                        <button type="button" onClick={() => toggle(c.id, "audio")}
                                            title={isOpen("audio") ? "Close recording" : "Play recording"}
                                            className="p-1.5 rounded-full text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                            {isOpen("audio") ? <X className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                                        </button>
                                    )}
                                </div>
                                {/* Mounted only on demand — each player downloads the whole file. */}
                                {isOpen("audio") && (
                                    <RecordingPlayer callId={c.id} endpoint={RECORDING_ENDPOINT} />
                                )}
                                {isOpen("transcript") && (
                                    <TranscriptView callId={c.id} className="mt-2 pl-6" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
